import { useState } from 'react';
import PhoneInput from './PhoneInput';
import { useAuth } from '../context/AuthContext';
import { useDialog } from '../context/DialogContext';
import authService from '../services/authService';
import { A, Card, CardTitle, Input, Pill, Prose, StatusPill } from './account/kit';

/**
 * Settings › Account › Phone number (Sept 2026 redesign: one white card).
 *
 * A number is only ever "verified" by echoing back a texted code. VERIFY NUMBER on a
 * number already on file texts that number straight away and opens the inline code row
 * ("SENT TO •••• 2409"); ADD / CHANGE NUMBER go through the country + number field first.
 */

/** "+13603062409" → "+1 360 306 2409" (NANP); anything else is left as typed. */
export function prettyPhone(e164) {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164 || '');
  return m ? `+1 ${m[1]} ${m[2]} ${m[3]}` : (e164 || '');
}

export default function ChangePhoneSection({ pad, onVerifiedChange }) {
  const { user, refreshUser } = useAuth();
  const { showConfirm } = useDialog();

  const verified = !!user?.phoneVerified;
  // Replied STOP → Telnyx blocks every future text at the profile level, so a code
  // would silently never arrive. Surface it instead of leaving the recovery path dead.
  const optedOut = !!user?.smsOptedOut;
  const current = user?.phoneNumber || '';

  const [editing, setEditing] = useState(false);
  const [country, setCountry] = useState(user?.phoneCountry || 'CA');
  const [number, setNumber] = useState('');
  const [codeOpen, setCodeOpen] = useState(false);
  const [otp, setOtp] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const reset = () => {
    setEditing(false); setCodeOpen(false); setNumber(''); setOtp('');
    setError(''); setBusy(''); setMaskedPhone('');
  };

  // Text a code to `to` (the number on file, or the one just typed).
  const send = async (to, iso) => {
    setError(''); setBusy('send');
    const r = await authService.startPhoneVerification(to, iso);
    setBusy('');
    if (!r.success) { setError(r.error || 'Could not send the code.'); return; }
    setMaskedPhone(r.data?.maskedPhone || '');
    setOtp('');
    setEditing(false);
    setCodeOpen(true);
  };

  const confirm = async () => {
    setError(''); setBusy('confirm');
    const r = await authService.verifyPhoneOtp(otp.trim());
    setBusy('');
    if (!r.success) { setError(r.error || 'That code isn’t right.'); return; }
    await refreshUser?.();
    onVerifiedChange?.(true);
    setDone(true);
    reset();
    setTimeout(() => setDone(false), 4000);
  };

  const remove = async () => {
    const yes = await showConfirm({
      title: 'REMOVE PHONE NUMBER?',
      message: 'You’ll lose the ability to reset your password by text. If you also lose access to your email, nobody will be able to get you back into this account.',
      confirmLabel: 'REMOVE',
      cancelLabel: 'KEEP IT',
      variant: 'danger',
    });
    if (!yes) return;
    setBusy('remove');
    const r = await authService.removePhone();
    setBusy('');
    if (!r.success) { setError(r.error || 'Could not remove the number.'); return; }
    await refreshUser?.();
    reset();
  };

  const last4 = (masked) => (masked || current).replace(/\D/g, '').slice(-4);
  const pill = !current ? null : verified ? <StatusPill bg={A.verified}>VERIFIED</StatusPill> : <StatusPill bg={A.live}>NOT VERIFIED</StatusPill>;

  return (
    <Card pad={pad} data-testid="phone-card">
      <CardTitle right={pill}>PHONE NUMBER</CardTitle>

      {done && (
        <span style={{ padding: '10px 14px', borderRadius: 16, background: A.ink, color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: '.12em', alignSelf: 'flex-start' }}>PHONE VERIFIED.</span>
      )}

      {!editing && current && (
        <>
          <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: '.04em' }}>{prettyPhone(current)}</span>

          {optedOut && (
            <span style={{ padding: '10px 14px', borderRadius: 16, background: A.held, fontSize: 12, lineHeight: 1.5 }}>
              <b style={{ letterSpacing: '.08em' }}>TEXTS ARE TURNED OFF FOR THIS NUMBER.</b>{' '}
              You replied STOP, so we can’t send it a code — which means it can’t recover your account right now. Reply <b>START</b> to our texts to turn them back on.
            </span>
          )}

          {!verified && !codeOpen && (
            <Prose>A number only works for account recovery once you’ve confirmed a code we text to it.</Prose>
          )}
          {verified && (
            <Prose color={A.grey500}>You can use this number to reset your password if you ever lose access to your email.</Prose>
          )}

          {codeOpen && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <Input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={otp}
                onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                placeholder="6-DIGIT CODE"
                aria-label="6-digit code"
                autoFocus
                style={{ width: 200, fontSize: 16, letterSpacing: '.3em' }}
              />
              <Pill kind="primary" onClick={confirm} disabled={otp.length !== 6 || busy === 'confirm'}>{busy === 'confirm' ? 'CHECKING…' : 'CONFIRM'}</Pill>
              <span style={{ fontSize: 10, letterSpacing: '.12em', color: A.grey500 }}>SENT TO •••• {last4(maskedPhone)}</span>
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {!verified && !codeOpen && (
              <Pill kind="primary" onClick={() => send(current, user?.phoneCountry || country)} disabled={busy === 'send' || optedOut}>{busy === 'send' ? 'SENDING…' : 'VERIFY NUMBER'}</Pill>
            )}
            {codeOpen && (
              <Pill kind="secondary" onClick={() => send(current, user?.phoneCountry || country)} disabled={busy === 'send'}>{busy === 'send' ? 'SENDING…' : 'RESEND'}</Pill>
            )}
            {verified && !codeOpen && (
              <Pill kind="secondary" onClick={() => { setEditing(true); setCodeOpen(false); setNumber(''); setError(''); }}>CHANGE NUMBER</Pill>
            )}
            <Pill kind="secondary" onClick={remove} disabled={busy === 'remove'}>{busy === 'remove' ? '…' : 'REMOVE'}</Pill>
          </div>
        </>
      )}

      {!editing && !current && (
        <>
          <span style={{ fontSize: 12, color: A.grey500 }}>No number on file.</span>
          <Pill kind="primary" style={{ alignSelf: 'flex-start' }} onClick={() => { setEditing(true); setNumber(''); setError(''); }}>+ ADD NUMBER</Pill>
        </>
      )}

      {editing && (
        <>
          <PhoneInput
            country={country}
            number={number}
            onChange={({ country: c, number: n }) => { setCountry(c); setNumber(n); setError(''); }}
            disabled={busy === 'send'}
            error={error || null}
            autoFocus
          />
          <Prose color={A.grey500}>We’ll text you a 6-digit code to confirm it’s yours.</Prose>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <Pill kind="primary" onClick={() => send(number.trim(), country)} disabled={!number.trim() || busy === 'send'}>{busy === 'send' ? 'SENDING…' : 'SEND CODE'}</Pill>
            <Pill kind="secondary" onClick={reset} disabled={busy === 'send'}>CANCEL</Pill>
          </div>
        </>
      )}

      {error && !editing && <span role="alert" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: A.danger }}>{error}</span>}
    </Card>
  );
}
